// Thin wrapper for the portal-api Node server (backend.md §6, frontend.md §7).
// Everything else goes through the PocketBase SDK directly.

import { activePb } from './pb';

const apiBase = import.meta.env.VITE_API_BASE || '/api';

function authHeaders() {
  const token = activePb().authStore.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    ...authHeaders(),
  };

  const response = await fetch(`${apiBase}${path}`, {
    ...options,
    headers,
  });

  const contentType = response.headers.get('content-type') || '';
  const body = contentType.includes('application/json') ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    throw new Error(body?.error || `Request failed with status ${response.status}`);
  }

  return body ?? response;
}

export const portalApi = {
  // Email a client a fresh one-click OTP sign-in link (agency auth).
  // The linked account is created on first use (auth_magic_link_spec §4).
  sendPortalAccess: (clientId) => request(`/clients/${clientId}/send-portal-access`, {
    method: 'POST',
  }),

  // Mark a client thread read (agency auth, single auditable path)
  markMessagesRead: (messageId) => request(`/messages/${messageId}/read`, {
    method: 'POST',
  }),

  // Fire a test notification from the settings screen (agency auth)
  sendTestNotification: () => request('/notify-test', { method: 'POST' }),

  // Quotes (pass 5/7): AI-assisted draft + approved-quote conversion
  draftQuote: ({ tenant_id, client_id, job_description }) => request('/quotes/draft', {
    method: 'POST',
    body: JSON.stringify({ tenant_id, client_id, job_description }),
  }),

  convertQuote: (quoteId, target = 'both') => request(`/quotes/${quoteId}/convert`, {
    method: 'POST',
    body: JSON.stringify({ target }),
  }),

  // Stripe (pass 6): checkout session + Connect onboarding/status
  createCheckoutSession: (invoiceId) => request(`/invoices/${invoiceId}/create-checkout-session`, {
    method: 'POST',
    body: JSON.stringify({}),
  }),

  stripeConnectLink: () => request('/stripe/connect-link', { method: 'POST', body: JSON.stringify({}) }),

  stripeConnectStatus: () => request('/stripe/connect-status'),

  // AI assistant (pass 7 §7B): server-side LLM helpers — keys never leave the server
  aiTest: () => request('/ai/test', { method: 'POST', body: JSON.stringify({}) }),

  aiSuggestReply: (clientId) => request('/ai/suggest-reply', {
    method: 'POST',
    body: JSON.stringify({ client_id: clientId }),
  }),

  aiClientSummary: (clientId) => request('/ai/client-summary', {
    method: 'POST',
    body: JSON.stringify({ client_id: clientId }),
  }),

  // Photon (OSM) address autocomplete — proxied server-side (backend.md §6.7)
  geocode: (q, lat, lon) => {
    const params = new URLSearchParams({ q });
    if (lat != null && lon != null) {
      params.set('lat', String(lat));
      params.set('lon', String(lon));
    }
    return request(`/geocode?${params.toString()}`);
  },

  // Stream an invoice PDF and trigger a browser download
  async downloadInvoicePdf(invoiceId, filename = 'invoice.pdf') {
    const headers = authHeaders();

    const response = await fetch(`${apiBase}/invoices/${invoiceId}/pdf`, { headers });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || 'PDF download failed');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },

  // Certificate PDF (gas certs) — same authed pattern; opens in a new tab
  // for print/preview. Errors (incl. 422 statutory-incomplete) surface to
  // the caller for the UI to explain.
  async fetchCertificatePdf(certificateId) {
    const headers = authHeaders();
    const response = await fetch(`${apiBase}/certificates/${certificateId}/pdf`, { headers });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || `PDF fetch failed (${response.status})`);
    }
    return response.blob();
  },

  // Public invoice (unauthenticated) — share link view for clients.
  // Contract endpoint 1: GET /i/:token returns invoice + client + tenant.
  publicInvoice: (token) => request(`/i/${token}`),

  // Public Stripe Checkout (unauthenticated) — pay now button.
  // Contract endpoint 2: POST /i/:token/create-checkout-session.
  createPublicCheckoutSession: (token) => request(`/i/${token}/create-checkout-session`, {
    method: 'POST',
  }),
};
